import { useEffect, useRef } from "react";
import { useIsPortrait } from "../../lib/useIsPortrait";

/**
 * Full-bleed prerendered video wall. Instead of decoding a live 12-tile grid of <video>
 * elements, we ship a single pre-rendered mosaic loop (see tools/prerender-landing) and
 * let one <video> paint the whole wall — a portrait and a landscape variant, picked by
 * viewport orientation. Any dark tint, vignette or accent glow is the parent's business
 * as CSS overlays; only the decode cost (12 → 1) drops.
 *
 * EXTRACTED FROM Landing.tsx when the marketing front door was deleted (`/` is the
 * screen surface now). The wall outlived the page that commissioned it: AmbientShowcase
 * plays it behind the /views home, at half speed.
 */
export function LandingBackdrop({
  playbackRate = 1,
}: { playbackRate?: number } = {}) {
  const portrait = useIsPortrait();
  const base = portrait ? "backdrop-portrait" : "backdrop-landscape";
  /**
   * Playback rate is a property of the element, not an attribute, so React cannot set it
   * declaratively — it has to be assigned after the element exists. Re-applied on
   * `loadedmetadata` because loading a new source resets it to 1, which `key={base}`
   * makes routine: an orientation flip swaps the file and would otherwise silently
   * restore full speed.
   */
  const ref = useRef<HTMLVideoElement | null>(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    const apply = () => {
      v.playbackRate = playbackRate;
    };
    apply();
    v.addEventListener("loadedmetadata", apply);
    return () => v.removeEventListener("loadedmetadata", apply);
  }, [playbackRate, base]);

  return (
    <video
      ref={ref}
      key={base}
      className="h-full w-full object-cover"
      autoPlay
      muted
      loop
      playsInline
      preload="auto"
      poster={`/landing/${base}.jpg`}
      src={`/landing/${base}.mp4`}
    />
  );
}
